import { InformationAlertIcon } from "@/assets/images/svgIcons/informationAlertIcon"
import { Button } from "@/components/ui/baseUi/button"
import { withForm } from "@/components/ui/forms/hooks/useFormHook"
import { Text } from "@/components/ui/baseUi/text"
import { CoachOfferPricingOptionCard } from "@/modules/coachOffers/components/coachOfferPricingOptionCard"
import { CoachOfferPricingOptionForm } from "@/modules/coachOffers/components/coachOfferPricingOptionForm"
import { coachOfferFormDefaultValues } from "@/modules/coachOffers/types/coachOfferFormTypes"
import { useFinanceSidebar } from "@/modules/finances/hooks/useFinanceSidebar"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { useUser } from "@/modules/users/hooks/useUser"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { BanknoteIcon, PlusIcon } from "lucide-react"

export const CoachOfferStep4View = withForm({
  defaultValues: coachOfferFormDefaultValues,
  render: function CoachOfferStep4ViewInner({ form }) {
    const { navigateToFinanceFeesLayout } = useFinanceSidebar()
    const { addSidebarStackOnTop } = useSidebar()
    const language = useLanguage()
    const user = useUser()
    const currency = user.stripeAccountCurrency ?? "EUR"

    function handleAddOption() {
      addSidebarStackOnTop({
        content: (
          <CoachOfferPricingOptionForm
            onValidSubmit={(values) => {
              form.pushFieldValue("paymentOffers", values)
            }}
          />
        ),
      })
    }

    function handleEditOption(index: number) {
      const currentValues = form.getFieldValue("paymentOffers")[index]
      addSidebarStackOnTop({
        content: (
          <CoachOfferPricingOptionForm
            defaultValues={currentValues}
            onValidSubmit={(values) => {
              const paymentOffers = form.getFieldValue("paymentOffers")
              paymentOffers[index] = values
              form.setFieldValue("paymentOffers", paymentOffers)
            }}
          />
        ),
      })
    }

    return (
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center gap-2.5">
            <BanknoteIcon color="currentColor" className="size-6" />
            <Text fontStyle="TM">{word("coach.offers.form.pricing.title")}</Text>
          </div>
          <Text fontStyle="BS" className="text-textOnSurface-subtle">
            {word("coach.offers.form.pricing.description")}
          </Text>
        </div>

        <form.Subscribe selector={(state) => state.values.paymentOffers}>
          {(paymentOffers) =>
            paymentOffers.length > 0 ? (
              <div className="flex flex-col gap-3">
                {paymentOffers.map((offer, index) => (
                  <CoachOfferPricingOptionCard
                    key={index}
                    offer={offer}
                    currency={currency}
                    locale={language}
                    onEdit={() => {
                      handleEditOption(index)
                    }}
                    onDelete={() => void form.removeFieldValue("paymentOffers", index)}
                  />
                ))}
              </div>
            ) : null
          }
        </form.Subscribe>

        <Button variant="outline" className="w-full gap-2" onClick={handleAddOption} size="lg">
          <PlusIcon size={14} />
          {word("coach.offers.form.pricing.addOption")}
        </Button>

        <div className="relative flex flex-col gap-4 overflow-hidden rounded-3xl bg-linear-96 from-surface-background-primary-information-secondary to-surface-background-primary-information-default px-6 py-5 before:absolute before:top-0 before:left-0 before:h-full before:w-1.5 before:bg-surface-background-primary-default">
          <div className="flex flex-row items-center gap-4">
            <div className="shrink-0">
              <InformationAlertIcon
                backgroundColor={"var(--surface-background-primary-default)"}
                color={"var(--color-interactive-text-primary-default)"}
              />
            </div>
            <div className="flex flex-col">
              <Text fontStyle="TM" className="text-textOnSurface-default">
                {word("coach.offers.form.pricing.infoBox.title")}
              </Text>
              <Text fontStyle="BM" className="text-textOnSurface-subtle">
                {word("coach.offers.form.pricing.infoBox.description")}
              </Text>
            </div>
          </div>
          <div className="pl-17">
            <Button
              variant="primary"
              size="md"
              className="w-fit"
              onClick={() => {
                navigateToFinanceFeesLayout(true)
              }}
            >
              {word("coach.offers.form.pricing.infoBox.cta")}
            </Button>
          </div>
        </div>
      </div>
    )
  },
})
